import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Eye, EyeOff } from 'lucide-react';

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error: authError, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [logoutMessage, setLogoutMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('message') === 'concurrent_logout') {
      setLogoutMessage('Multiple login sessions detected. You have been logged out from this session.');
    }
  }, [location]);

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
    return () => clearError();
  }, [authError, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-6">
      <div className="w-full max-w-md bg-surface-container-lowest rounded-2xl shadow-xl p-8 animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-black text-navy tracking-tight">
            Agent Capital
          </h1>
          <p className="text-sm text-secondary mt-2">Sign in to your account</p>
        </div>

        {logoutMessage && (
          <div className="mb-6 text-sm text-warning bg-warning/10 p-3 rounded-xl border border-warning/20 text-center font-semibold">
            {logoutMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-secondary block mb-1.5">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="customer@example.com"
              aria-label="Email address"
              className="w-full py-3 px-4 bg-surface-container-low rounded-xl border-0 text-sm focus:ring-2 focus:ring-primary/25 outline-none"
              required
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-secondary block mb-1.5">
              Password
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••••••••"
              className="w-full py-3 px-4 bg-surface-container-low rounded-xl border-0 text-sm focus:ring-2 focus:ring-primary/25 outline-none pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-[2.3rem] text-secondary hover:text-on-surface"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="text-right -mt-2">
            <Link to="/forgot-password" className="text-xs text-primary hover:underline">
              Forgot password?
            </Link>
          </div>

          {error && (
            <p className="text-sm text-error bg-error-container/10 p-3 rounded-xl">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-primary-container text-on-primary font-bold text-sm disabled:opacity-50 hover:opacity-90 active:scale-95 transition-all shadow-lg"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
